import { useMemo } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';
import { useFragment } from 'react-relay';
import Slider from 'react-slick';
import { graphql } from 'relay-runtime';

import { N500 } from '@atlaskit/theme/colors';

import { collectionCardCarouselContentfulRelayFragment$key } from '@/__generated_contentful__/collectionCardCarouselContentfulRelayFragment.graphql';
import { ConfluenceTemplateMessages } from '@/components/confluence/templates/template-messages';
import { ConfluenceTemplateAnalytics } from '@/components/confluence/templates/util/confluence-templates-analytics';

import { CollectionCardRelay } from './collection-card-contentful-relay';

import type { Settings as SlickSettings } from 'react-slick';

// Source https://github.com/kenwheeler/slick/blob/master/slick/slick.css
const slickStyle = css({
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slider': {
		position: 'relative',
		display: 'block',
		boxSizing: 'border-box',
		WebkitUserSelect: 'none',
		MozUserSelect: 'none',
		MsUserSelect: 'none',
		userSelect: 'none',
		WebkitTouchCallout: 'none',
		KhtmlUserSelect: 'none',
		MsTouchAction: 'pan-y',
		touchAction: 'pan-y',
		WebkitTapHighlightColor: 'transparent',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-list': {
		position: 'relative',
		display: 'block',
		overflow: 'hidden',
		margin: 0,
		padding: 0,
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-list:focus': {
		outline: 'none',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-list.dragging': {
		cursor: 'pointer',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slider .slick-track, .slick-slider .slick-list': {
		WebkitTransform: 'translate3d(0, 0, 0)',
		MozTransform: 'translate3d(0, 0, 0)',
		MsTransform: 'translate3d(0, 0, 0)',
		OTransform: 'translate3d(0, 0, 0)',
		transform: 'translate3d(0, 0, 0)',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-track': {
		position: 'relative',
		insetBlockStart: 0,
		insetInlineStart: 0,
		display: 'block',
		marginInlineStart: 'auto',
		marginInlineEnd: 'auto',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-track:before, .slick-track:after': {
		display: 'table',
		content: "''",
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-track:after': {
		clear: 'both',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-loading .slick-track': {
		visibility: 'hidden',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slide': {
		display: 'none',
		float: 'left',
		height: '100%',
		minHeight: '1px',
	},

	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slide > div': {
		display: 'flex',
		justifyContent: 'center',
	},

	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"[dir='rtl'] .slick-slide": {
		float: 'right',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slide img': {
		display: 'block',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slide.slick-loading img': {
		display: 'none',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slide.dragging img': {
		pointerEvents: 'none',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-initialized .slick-slide': {
		display: 'block',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-loading .slick-slide': {
		visibility: 'hidden',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-vertical .slick-slide': {
		display: 'block',
		height: 'auto',
		border: '1px solid transparent',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-arrow.slick-hidden': {
		display: 'none',
	},
});

const carouselContainer = css({
	position: 'relative',

	width: 1200,
	'@media (max-width: 639.99px)': {
		width: 368,
	},
	'@media (min-width: 640px) and (max-width: 767.99px)': {
		width: 624,
	},
	'@media (min-width: 768px) and (max-width: 1023.99px)': {
		width: 784,
	},
	'@media (min-width: 1024px) and (max-width: 1279.99px)': {
		width: 800,
	},
});

const carouselCardWrapper = css({
	display: 'flex !important',
	justifyContent: 'center',
	margin: '8px 16px',

	'@media (max-width: 767.99px)': {
		margin: '8px',
	},
});

const CollectionCardCarouselFragment = graphql`
	fragment collectionCardCarouselContentfulRelayFragment on TemplateCollection
	@relay(plural: true) {
		sys {
			id
		}
		...collectionCardContentfulRelayFragment
	}
`;

export type CollectionCardCarouselProps = {
	collections: collectionCardCarouselContentfulRelayFragment$key;
};

export const CollectionCardCarousel = ({ collections }: CollectionCardCarouselProps) => {
	const data = useFragment(CollectionCardCarouselFragment, collections);
	const settings: SlickSettings = useMemo(
		() => ({
			infinite: Array.isArray(data) && data.length > 3,
			slidesToShow: 3,
			slidesToScroll: 1,
			prevArrow: <LeftArrow />,
			nextArrow: <RightArrow />,
			responsive: [
				{
					breakpoint: 1280,
					settings: {
						slidesToShow: 2,
						slidesToScroll: 1,
						infinite: Array.isArray(data) && data.length > 2,
					},
				},
				{
					breakpoint: 800,
					settings: {
						slidesToShow: 1,
						slidesToScroll: 1,
						infinite: Array.isArray(data) && data.length > 1,
					},
				},
			],
		}),
		[data],
	);

	const beforeChange = (currentSlide: number, nextSlide: number) => {
		ConfluenceTemplateAnalytics.sendCollectionCarouselAnalytic(
			currentSlide,
			nextSlide,
			Array.isArray(data) ? data.length : 0,
		);
	};

	return (
		<div css={carouselContainer}>
			<div css={slickStyle}>
				<Slider {...settings} beforeChange={beforeChange}>
					{Array.isArray(data) &&
						data.map((collection) => (
							<div css={carouselCardWrapper} key={collection.sys.id}>
								<CollectionCardRelay collection={collection} />
							</div>
						))}
				</Slider>
			</div>
		</div>
	);
};

const buttonLeft = css({
	backgroundColor: '#ffffff',
	display: 'block',
	position: 'absolute',
	boxSizing: 'border-box',
	insetBlockStart: '50%',
	border: 'none',
	boxShadow: '1px 1px 6px 0 rgba(0,0,0,.2)',
	transform: 'translateY(-40%)',
	borderRadius: '50%',
	outline: 'none',
	cursor: 'pointer',
	color: '#0052cc',
	lineHeight: 28,
	zIndex: 1,
	'@media (max-width: 767.99px)': {
		insetInlineStart: -8,
	},
	width: 40,
	height: 40,
	insetInlineStart: -4,
});
const buttonLeftArrow = css({
	transform: 'rotate(-45deg)',
	width: 12,
	height: 12,
	display: 'inline-block',
	lineHeight: 0,
	position: 'absolute',
	marginBlockStart: -6,
	marginInlineStart: -4,
	boxSizing: 'border-box',
	borderBlockStart: '3px solid',
	borderInlineStart: '3px solid',
	fontSize: 16,
	color: N500,
});
const LeftArrow = ({ onClick }: { onClick?: () => void }) => {
	const intl = useIntl();
	const previousLabel = intl.formatMessage(ConfluenceTemplateMessages.carouselPreviousLabel);
	return (
		<button css={buttonLeft} aria-label={previousLabel} onClick={onClick}>
			<span css={buttonLeftArrow} />
		</button>
	);
};

const buttonRight = css({
	backgroundColor: '#ffffff',
	boxSizing: 'border-box',
	display: 'block',
	position: 'absolute',
	insetBlockStart: '50%',
	border: 'none',
	boxShadow: '1px 1px 6px 0 rgba(0,0,0,.2)',
	transform: 'translateY(-40%)',
	borderRadius: '50%',
	outline: 'none',
	cursor: 'pointer',
	color: '#0052cc',
	lineHeight: 28,
	zIndex: 1,

	'@media (max-width: 767.99px)': {
		insetInlineEnd: -8,
	},
	width: 40,
	height: 40,
	insetInlineEnd: -4,
});
const buttonRightArrow = css({
	transform: 'rotate(135deg)',
	width: 12,
	height: 12,
	display: 'inline-block',
	lineHeight: 0,
	position: 'absolute',
	marginBlockStart: -6,
	marginInlineStart: -8,
	boxSizing: 'border-box',
	borderBlockStart: '3px solid',
	borderInlineStart: '3px solid',
	fontSize: 16,
	color: N500,
});
const RightArrow = ({ onClick }: { onClick?: () => void }) => {
	const intl = useIntl();
	const nextLabel = intl.formatMessage(ConfluenceTemplateMessages.carouselNextLabel);
	return (
		<button css={buttonRight} aria-label={nextLabel} onClick={onClick}>
			<span css={buttonRightArrow} />
		</button>
	);
};
